/**
 * Handler: view_item_list event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { trackKlaviyoViewCategoryDirect } from '../fallbacks/klaviyo';
import { sendGA4Fallback, mapGA4Item } from '../fallbacks/ga4';
import { generateEventId, gtmMarketFields } from '../utils';
import { extractNumericId } from '../../adapters/ids';

export function handleViewItemList(
  analyzify: WindowAnalyzify,
  products: StorefrontProduct[],
  listName: string,
  listId?: string
): void {
  // Always use window.analyzify to ensure we have the current reference
  // This is important in case the dispatcher's internal reference is stale
  const winAnalyzify = (window as any).analyzify as WindowAnalyzify;
  if (!winAnalyzify) {
    console.warn('trackViewItemList: window.analyzify not found');
    return;
  }

  const adaptedProducts = products.map((p) => adaptProduct(p).product);

  // Get collection object from window.analyzify if set via setPageContext
  // CDN scripts expect this structure for collection pages
  const existingCollectionObj = (winAnalyzify as any).getCollectionObj;
  const onCollectionPage = (winAnalyzify as any).shopify_template === 'collection';

  const collectionId = extractNumericId(listId);

  // Use the page's collection (set via setPageContext) only for the list that
  // IS that collection. Any other list — recommendations on a product page, a
  // list after navigating away from a collection — is reported as given.
  const existingId = existingCollectionObj?.id || existingCollectionObj?.collection?.id || 0;
  const hasValidProducts = (existingCollectionObj?.collection?.products?.length > 0 ||
                            existingCollectionObj?.products?.length > 0) &&
    onCollectionPage &&
    (!collectionId || !existingId || String(existingId) === String(collectionId));
  const collectionHandle = listName.toLowerCase().replace(/\s+/g, '-');

  const getCollectionObj = hasValidProducts ? existingCollectionObj : {
    collection: {
      id: collectionId,
      title: listName,
      handle: collectionHandle,
      products: adaptedProducts,
      all_products_count: adaptedProducts.length,
      products_count: adaptedProducts.length,
    },
    // Top-level fields required by CDN's gaViewItemList which destructures:
    // const { products, id, title, handle } = ga4Obj.getCollectionObj;
    products: adaptedProducts,
    id: collectionId,
    title: listName,
    handle: collectionHandle,
    collection_sku_list: [],
  };

  // CRITICAL: Ensure products array exists at ALL expected locations
  // Different CDN scripts may read from different paths
  if (!getCollectionObj.collection) {
    getCollectionObj.collection = {
      id: 0,
      title: listName,
      handle: listName.toLowerCase().replace(/\s+/g, '-'),
      products: adaptedProducts,
      all_products_count: adaptedProducts.length,
      products_count: adaptedProducts.length,
    };
  }
  if (!Array.isArray(getCollectionObj.collection.products)) {
    getCollectionObj.collection.products = adaptedProducts;
  }
  // Some CDN functions read products from root level
  if (!Array.isArray(getCollectionObj.products)) {
    getCollectionObj.products = adaptedProducts;
  }
  // CDN's gaViewItemList destructures { products, id, title, handle } from root level
  // Ensure these exist even when using existingCollectionObj from setPageContext
  if (!getCollectionObj.title && getCollectionObj.collection?.title) {
    getCollectionObj.title = getCollectionObj.collection.title;
  }
  if (!getCollectionObj.id && getCollectionObj.collection?.id) {
    getCollectionObj.id = getCollectionObj.collection.id;
  }
  if (!getCollectionObj.handle && getCollectionObj.collection?.handle) {
    getCollectionObj.handle = getCollectionObj.collection.handle;
  }

  // Ensure getCollectionObj is set on window.analyzify before calling CDN functions
  // CDN functions read from window.analyzify directly via their internal reference.
  // A list that is not the page's collection is temporary: the page's
  // context is restored after the calls below.
  const isTemporaryList = getCollectionObj !== existingCollectionObj;
  (winAnalyzify as any).getCollectionObj = getCollectionObj;
  // One event ID for the browser (GTM ss_event_id) and server-side event
  const listEventId = (winAnalyzify as any).eventId?.get?.('view_item_list') || generateEventId();

  // Also set collection object at window.analyzify.collection for CDN functions that read from there
  if (!(winAnalyzify as any).collection) {
    (winAnalyzify as any).collection = getCollectionObj.collection;
  } else if (!Array.isArray((winAnalyzify as any).collection.products)) {
    (winAnalyzify as any).collection.products = adaptedProducts;
  }

  // GA4 - Try CDN function first, fall back to direct gtag if it fails
  const props = winAnalyzify.properties;
  let ga4Success = false;
  if (typeof winAnalyzify.gaViewItemList === 'function') {
    try {
      winAnalyzify.gaViewItemList();
      ga4Success = true;
    } catch (error) {
      console.warn('gaViewItemList CDN error, falling back to direct gtag:', error);
    }
  }

  // Fallback: Call gtag directly if CDN function failed or doesn't exist
  if (!ga4Success) {
    const col = getCollectionObj.collection;
    const colListId = col.id || collectionId || null;
    const colListName = col.title || listName;
    const ga4Items = adaptedProducts.map((p: any, i: number) => {
      const firstVariant = p.variants?.[0] || {};
      return mapGA4Item(winAnalyzify, p, firstVariant, i, colListId, colListName);
    });
    // CDN: formatPrice(products.reduce((acc, curr) => acc + formatPrice(curr.price), 0), true)
    const rawValue = adaptedProducts.reduce(
      (sum: number, p: any) => sum + (winAnalyzify.formatPrice?.(p.price) || 0),
      0
    );

    sendGA4Fallback(winAnalyzify, 'view_item_list', {
      currency: winAnalyzify.currency || 'USD',
      item_list_id: colListId,
      item_list_name: colListName,
      value: winAnalyzify.formatPrice?.(rawValue, true) ?? rawValue,
      total_quantity: adaptedProducts.length || 1,
      total_items: adaptedProducts.length || 1,
      items: ga4Items,
    });
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  const win = window as any;
  let gtmTracked = false;
  if (typeof winAnalyzify.gtmViewItemList === 'function') {
    try {
      const col = getCollectionObj.collection;
      winAnalyzify.gtmViewItemList(col.title, col.id, col.handle, col.products);
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && winAnalyzify.properties?.GTM?.status) {
    const col = getCollectionObj.collection;
    const colProducts = col?.products || adaptedProducts;
    const title = col?.title || listName;
    const id = col?.id || collectionId;
    const handle = col?.handle || listName.toLowerCase().replace(/\s+/g, '-');

    const items = colProducts.map((item: any, index: number) => {
      const firstVariant = item.variants?.[0];
      const actualPrice = winAnalyzify.formatPrice?.(firstVariant?.price ?? item?.price, true) || 0;
      const compareAtPrice = winAnalyzify.formatPrice?.(item?.compare_at_price, true) || 0;
      const getDiscount = (compareAtPrice > 0 && compareAtPrice !== actualPrice) ? compareAtPrice - actualPrice : 0;

      return {
        id: winAnalyzify.getShopifyId?.(
          winAnalyzify.feed_region || '',
          item?.id,
          firstVariant?.id,
          firstVariant?.sku
        ) || null,
        item_id: item?.id?.toString() || null,
        item_name: item?.title?.trim() || null,
        item_list_name: title?.trim() || null,
        item_list_id: id?.toString() || null,
        item_list_handle: handle || null,
        index,
        item_barcode: firstVariant?.barcode || null,
        discount: winAnalyzify.formatPrice?.(getDiscount, false) || 0,
        price: actualPrice || 0,
        item_category: item?.product_type || item?.type || null,
        item_brand: item?.vendor?.trim() || null,
        item_variant: firstVariant?.title?.trim() || null,
        item_variant_id: firstVariant?.id?.toString() || null,
        item_sku: firstVariant?.sku || null,
        quantity: 1,
      };
    });

    if (items.length > 0) {
      win.dataLayer.push({ ecommerce: null });
      win.dataLayer.push({
        event: 'ee_view_item_list',
        ss_event_id: listEventId,
        ecommerce: {
          analyzify_source: 'advanced',
          implementation_type: 'extension',
          item_list_name: title || handle || null,
          item_list_id: id || null,
          items,
          cart_id: winAnalyzify.cart_id || null,
        },
        ...gtmMarketFields(winAnalyzify),
      });
    }
  }

  // Facebook - ViewCollection event for collection pages
  if (typeof winAnalyzify.fbViewCollection === 'function') {
    try {
      winAnalyzify.fbViewCollection(getCollectionObj);
    } catch (error) {
      console.warn('fbViewCollection error (non-critical):', error);
    }
  }

  // Bing - wrap in try-catch as CDN script may expect specific data structure
  if (typeof winAnalyzify.bingViewItemList === 'function') {
    try {
      winAnalyzify.bingViewItemList(getCollectionObj);
    } catch (error) {
      console.warn('bingViewItemList error (non-critical):', error);
    }
  }

  // Klaviyo - try CDN first, fall back to direct API
  const klaviyoPropsVIL = analyzify.properties?.KLAVIYO;
  if (klaviyoPropsVIL?.status && klaviyoPropsVIL.events?.view_item_list) {
    if (typeof winAnalyzify.klaviyoViewCategory === 'function') {
      try {
        winAnalyzify.klaviyoViewCategory(getCollectionObj);
      } catch (error) {
        trackKlaviyoViewCategoryDirect(analyzify, getCollectionObj.collection?.title || listName);
      }
    } else {
      trackKlaviyoViewCategoryDirect(analyzify, getCollectionObj.collection?.title || listName);
    }
  }

  // Criteo
  if (typeof winAnalyzify.criteoViewList === 'function') {
    try {
      winAnalyzify.criteoViewList(getCollectionObj);
    } catch (error) {
      console.warn('criteoViewList error (non-critical):', error);
    }
  }

  // Server-side — the landing collection's view_item_list is sent by the CDN
  // during platform init; SPA list views are sent here.
  if (typeof (winAnalyzify as any).ssViewItemList === 'function' && props?.SERVERSIDE?.status) {
    try {
      (winAnalyzify as any).ssViewItemList(getCollectionObj, listEventId);
    } catch (error) {
      console.warn('ssViewItemList error (non-critical):', error);
    }
  }

  if (isTemporaryList) {
    (winAnalyzify as any).getCollectionObj = existingCollectionObj;
  }
}
